/**
 * InterventionBanner — CRM-UX-03
 *
 * Shown on Opportunity / Booking / Lead detail pages when the authenticated
 * user is acting as BUSINESS_OWNER on a record assigned to a different RM.
 *
 * Governance:
 *  - Purely informational — no action gating here
 *  - Financial controls (booking.confirm, ledger.approve) are NOT affected
 *  - Every displayed action is logged server-side via audit_events
 */
import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { responsibilityApi } from "@/services/api";

const T = {
  amber: "#92400E",
  amberBg: "#FEF3C7",
  amberBorder: "#FCD34D",
  gold: "#C9A15F",
};

/**
 * Props:
 *   recordType : "lead" | "opportunity" | "booking" | "event"
 *   recordId   : string  — the record's _id / id
 *   dismissable: bool    — whether the user can dismiss the banner (default true)
 */
export function InterventionBanner({ recordType, recordId, dismissable = true }) {
  const { user } = useAuth();
  const [resolution, setResolution] = useState(null);
  const [dismissed, setDismissed]   = useState(false);

  const load = useCallback(async () => {
    if (!recordId || !recordType || !user) return;
    // Only relevant for BUSINESS_OWNER
    if (user.role !== "BUSINESS_OWNER") return;
    try {
      const r = await responsibilityApi.getResponsibility(recordType, recordId);
      setResolution(r.data);
    } catch {
      // Non-blocking — if resolution fails, don't show banner
    }
  }, [recordType, recordId, user]);

  useEffect(() => { load(); }, [load]);

  // Reset dismissed when record changes
  useEffect(() => { setDismissed(false); }, [recordId]);

  if (!resolution || !resolution.is_intervention || dismissed) return null;

  const ownerLabel = resolution.assigned_to_name
    ? `${resolution.assigned_to_name}'s record`
    : "another user's record";

  return (
    <div
      data-testid="intervention-banner"
      className="flex items-start gap-3 px-4 py-3 rounded-lg border text-sm"
      style={{
        background: T.amberBg,
        borderColor: T.amberBorder,
        color: T.amber,
      }}
      role="status"
      aria-label="Business Owner Operational Authority active"
    >
      <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-xs leading-snug">
          Business Owner Operational Authority
        </p>
        <p className="text-[11px] mt-0.5 leading-relaxed">
          You are acting on {ownerLabel}. All mutations are audit-logged.
        </p>
      </div>
      {dismissable && (
        <button
          data-testid="intervention-banner-dismiss"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
